/**
 * Handle CF7 success event for the home contact form.
 *
 * Redirects the submitted data to WhatsApp after a successful send.
 *
 * @param {Document} root
 */
function formatBrazilPhone(digits) {
  const normalized = digits.startsWith("55") ? digits.slice(2) : digits;
  const trimmed = normalized.slice(0, 11);

  if (!trimmed) {
    return "";
  }

  if (trimmed.length <= 2) {
    return `+(55) ${trimmed}`;
  }

  const areaCode = trimmed.slice(0, 2);
  const subscriber = trimmed.slice(2);

  if (!subscriber) {
    return `+(55) ${areaCode}`;
  }

  if (subscriber.length <= 8) {
    if (subscriber.length <= 4) {
      return `+(55) ${areaCode} ${subscriber}`;
    }

    return `+(55) ${areaCode} ${subscriber.slice(0, 4)}-${subscriber.slice(4)}`;
  }

  if (subscriber.length <= 5) {
    return `+(55) ${areaCode} ${subscriber.slice(0, 1)} ${subscriber.slice(1)}`;
  }

  if (subscriber.length <= 9) {
    return `+(55) ${areaCode} ${subscriber.slice(0, 1)} ${subscriber.slice(1, 5)}-${subscriber.slice(5)}`;
  }

  return `+(55) ${areaCode} ${subscriber.slice(0, 4)}-${subscriber.slice(4, 8)}`;
}

function formatUsPhone(digits) {
  const normalized =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  const trimmed = normalized.slice(0, 10);

  if (!trimmed) {
    return "";
  }

  if (trimmed.length <= 3) {
    return `+(${trimmed})`;
  }

  if (trimmed.length <= 6) {
    return `+(${trimmed.slice(0, 3)}) ${trimmed.slice(3)}`;
  }

  return `+(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6)}`;
}

function shouldUseBrazilianPhoneMask() {
  const language = document.documentElement.lang || "";

  return language.toLowerCase().startsWith("pt");
}

function formatPhoneValue(value, useBrazilianMask = true) {
  const raw = typeof value === "string" ? value.trim() : "";
  const digits = raw.replace(/\D+/g, "");

  if (!digits) {
    return "";
  }

  if (!useBrazilianMask) {
    return formatUsPhone(digits);
  }

  return formatBrazilPhone(digits);
}

function initPhoneMask(input) {
  if (!(input instanceof Element) || input.tagName !== "INPUT") {
    return;
  }

  if (input.dataset.phoneMaskInitialized === "true") {
    return;
  }

  input.dataset.phoneMaskInitialized = "true";
  input.setAttribute("inputmode", "tel");
  input.setAttribute("autocomplete", "tel");
  input.setAttribute("maxlength", "22");

  const applyMask = () => {
    input.value = formatPhoneValue(input.value, shouldUseBrazilianPhoneMask());
  };

  input.addEventListener("input", applyMask);
  input.addEventListener("blur", applyMask);
  applyMask();
}

export function initContactForm(root = document) {
  const contactForms = root.querySelectorAll("[data-contact-form]");

  if (!contactForms.length || window.__fastersContactFormInitialized) {
    return;
  }

  contactForms.forEach((container) => {
    const phoneInput = container.querySelector("input.masc-celular");

    if (phoneInput) {
      initPhoneMask(phoneInput);
    }
  });

  window.__fastersContactFormInitialized = true;

  document.addEventListener(
    "wpcf7mailsent",
    (event) => {
      const form = event.target;

      if (!(form instanceof Element) || form.tagName !== "FORM") {
        return;
      }

      const container = form.closest("[data-contact-form]");

      if (!container) {
        return;
      }

      const inputs = Array.isArray(event.detail?.inputs)
        ? event.detail.inputs
        : [];
      const values = {
        nome: "",
        empresa: "",
        email: "",
        phone: "",
        message: "",
        lgpd: "Nao",
      };
      const duplicateNameValues = [];
      const privacyAccepted = Boolean(
        form.querySelector(
          'input[name="checkbox"]:checked, input[name="checkbox[]"]:checked',
        ),
      );

      inputs.forEach((input) => {
        if (!input?.name) {
          return;
        }

        const inputValue =
          typeof input.value === "string" ? input.value.trim() : "";

        if (input.name === "nome") {
          duplicateNameValues.push(inputValue);

          if (!values.nome) {
            values.nome = inputValue;
          }

          return;
        }

        if (input.name === "empresa") {
          values.empresa = inputValue;
          return;
        }

        if (input.name === "email") {
          values.email = inputValue;
          return;
        }

        if (input.name === "phone") {
          values.phone = inputValue;
          return;
        }

        if (input.name === "message") {
          values.message = inputValue;
          return;
        }

        if (input.name === "checkbox" || input.name === "checkbox[]") {
          values.lgpd = inputValue ? "Sim" : "Nao";
        }
      });

      if (!values.empresa && duplicateNameValues.length > 1) {
        values.empresa = duplicateNameValues[1];
      }

      values.lgpd = privacyAccepted ? "Sim" : values.lgpd;

      const whatsappPhone = (container.dataset.whatsappNumber || "").replace(
        /\D+/g,
        "",
      );

      if (!whatsappPhone) {
        return;
      }

      const greeting =
        container.dataset.whatsappGreeting || "Novo contato pelo site:";

      const messageLines = [
        greeting,
        `Nome: ${values.nome}`,
        `Empresa: ${values.empresa}`,
        `Email: ${values.email}`,
        `Telefone: ${values.phone}`,
        `Mensagem: ${values.message}`,
        `Aceitou os Termos de Privacidade: ${values.lgpd}`,
      ];

      const whatsappUrl = new window.URL("https://api.whatsapp.com/send");
      whatsappUrl.searchParams.set("phone", whatsappPhone);
      whatsappUrl.searchParams.set("text", messageLines.join("\n"));

      window.open(whatsappUrl.toString(), "_blank", "noopener");
    },
    false,
  );
}
